import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Database, BarChart2, MapPin, Search } from 'lucide-react';
import Globe from './globalTweets.tsx';

function Home() {
    return (
        <div className="container mx-auto px-6 py-12">
            <section className="text-center mb-16">
                <h1 className="text-5xl md:text-7xl font-bold mb-6 gradient-text animate-pulse">
                    Real-time Tweet Analysis
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                    Uncover insights from Twitter data as it happens, powered by cutting-edge technology and real-time processing.
                </p>
                <Link to="/dashboard" className="button inline-flex items-center animate-bounce">
                    Explore Dashboard
                    <ArrowRight className="ml-2" />
                </Link>
            </section>

            <section className="mb-16 text-center">
                <h2 className="text-3xl font-bold mb-8 gradient-text">Explore Tweet Insights</h2>
                <form action="/dashboard" className="max-w-2xl mx-auto">
                    <div className="flex items-center border-2 border-indigo-500 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <input
                            type="text"
                            name="keyword"
                            placeholder="Enter a keyword to analyze tweets..."
                            className="flex-grow px-6 py-4 text-lg focus:outline-none"
                        />
                        <button
                            type="submit"
                            className="bg-indigo-500 text-white px-6 py-4 hover:bg-indigo-600 transition-colors duration-300"
                        >
                            <Search className="w-6 h-6" />
                            <span className="sr-only">Search</span>
                        </button>
                    </div>
                </form>
            </section>

            <section className="mb-16">
                <h2 className="text-3xl font-bold mb-8 text-center gradient-text">How It Works</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {[
                        {
                            icon: <Zap className="w-12 h-12 text-indigo-600" />,
                            title: "Tweet Ingestion",
                            description: "Real-time data ingestion via Apache Kafka",
                        },
                        {
                            icon: <Database className="w-12 h-12 text-pink-500" />,
                            title: "Stream Processing",
                            description: "High-speed analysis with Apache Spark",
                        },
                        {
                            icon: <BarChart2 className="w-12 h-12 text-indigo-600" />,
                            title: "Sentiment Analysis",
                            description: "Advanced NLP for tweet sentiment classification",
                        },
                        {
                            icon: <MapPin className="w-12 h-12 text-pink-500" />,
                            title: "Geospatial Insights",
                            description: "Location-based trend and sentiment mapping",
                        },
                    ].map((item, index) => (
                        <div
                            key={index}
                            className="card p-6 text-center group hover:bg-indigo-50 transition-all duration-300 transform hover:scale-105"
                        >
                            <div className="mb-4 flex justify-center transform group-hover:rotate-12 transition-transform duration-300">
                                {item.icon}
                            </div>
                            <h3 className="text-xl font-semibold mb-2 text-gray-800">{item.title}</h3>
                            <p className="text-gray-600">{item.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="text-center bg-white bg-opacity-50 backdrop-blur-md rounded-lg p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <h2 className="text-3xl font-bold mb-4 gradient-text">Ready to dive deeper?</h2>
                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                    Explore our real-time dashboard and uncover the power of tweet analysis. See trends, sentiments, and insights as they happen.
                </p>
                <Link
                    to="/about"
                    className="inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-700 transition duration-300 group"
                >
                    Learn more about our technology
                    <ArrowRight className="ml-2 transform group-hover:translate-x-2 transition-transform duration-300" />
                </Link>
            </section>
        </div>
    );
}

export default Home;
